/** @jsxImportSource @emotion/react */

import { useTheme } from '@emotion/react';
import { fabric } from 'fabric';
import { Object } from 'fabric/fabric-impl';
import { Circle, Diamond, Hexagon, Pentagon, Square, Squircle, Star, Triangle, TriangleRight } from 'lucide-react';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import React, { useCallback, useState } from 'react';
import type { MessageDescriptor } from 'shared/i18n/i18n';
import { useDispatch, useSelector } from 'react-redux';
import { useCloseOnOutsideOrEsc } from 'shared/utils';
import customization from 'modules/overlay-canvas/addons/customization';
import history from 'modules/overlay-canvas/addons/history';
import SnapToGrid from 'modules/overlay-canvas/addons/snap-to-grid';
import UDIconButton from 'ud-ui/components/icon-button';
import UDTippy from 'ud-ui/components/tippy';
import { ModalState } from 'modules/template-editor/domain/interface/modal-state';
import { setActiveModal } from 'modules/template-editor/store/editor/editor';
import { selectActiveModal } from 'modules/template-editor/store/editor/selectors';

import BaseModal from 'modules/template-editor/ui/components/elements/modals/base-modal';
import { ModalButton, OpenerIconWrapper } from './styles';
import { useEditorManager } from 'modules/template-editor/ui/context';

export type ShapeType =
  | 'rect'
  | 'circle'
  | 'triangle'
  | 'right-triangle'
  | 'ellipse'
  | 'trapezoid'
  | 'pentagon'
  | 'hexagon'
  | 'star'
  | 'diamond';

interface ShapeConfig {
  type: ShapeType;
  label: MessageDescriptor;
  icon: React.ReactNode;
  create: (left: number, top: number) => Object;
}

function makePolygonPoints(sides: number, radius: number, cx: number, cy: number, offsetAngle = 0) {
  return Array.from({ length: sides }, (_, i) => {
    const angle = (i * 2 * Math.PI) / sides + offsetAngle;
    return { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
  });
}

function makeStarPoints(outerR: number, innerR: number, cx: number, cy: number) {
  return Array.from({ length: 10 }, (_, i) => {
    const angle = (i * Math.PI) / 5 - Math.PI / 2;
    const r = i % 2 === 0 ? outerR : innerR;
    return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
  });
}

const SHAPE_CONFIGS: ShapeConfig[] = [
  {
    type: 'rect',
    label: msg`Прямоугольник`,
    icon: <Square size={20} strokeWidth={1} />,
    create: (left, top) =>
      new fabric.Rect({
        width: 200,
        height: 100,
        fill: 'red',
        strokeWidth: 0,
        left,
        top,
        ...customization.getAllProperties(),
      }),
  },
  {
    type: 'circle',
    label: msg`Круг`,
    icon: <Circle size={20} strokeWidth={1} />,
    create: (left, top) =>
      new fabric.Circle({
        radius: 100,
        fill: 'red',
        strokeWidth: 0,
        left,
        top,
        ...customization.getAllProperties(),
      }),
  },
  {
    type: 'triangle',
    label: msg`Треугольник`,
    icon: <Triangle size={20} strokeWidth={1} />,
    create: (left, top) =>
      new fabric.Triangle({
        width: 200,
        height: 173,
        fill: 'red',
        strokeWidth: 0,
        left,
        top,
        ...customization.getAllProperties(),
      }),
  },
  {
    type: 'right-triangle',
    label: msg`Прямоугольный треугольник`,
    icon: <TriangleRight size={20} strokeWidth={1} />,
    create: (left, top) =>
      new fabric.Polygon(
        [
          { x: 0, y: 0 },
          { x: 0, y: 200 },
          { x: 200, y: 200 },
        ],
        {
          fill: 'red',
          strokeWidth: 0,
          left,
          top,
          ...customization.getAllProperties(),
        },
      ),
  },
  {
    type: 'trapezoid',
    label: msg`Трапеция`,
    icon: <Square size={20} strokeWidth={1} />,
    create: (left, top) =>
      new fabric.Polygon(
        [
          { x: 50, y: 0 },
          { x: 150, y: 0 },
          { x: 200, y: 100 },
          { x: 0, y: 100 },
        ],
        {
          fill: 'red',
          strokeWidth: 0,
          left,
          top,
          data: { shapeType: 'trapezoid' },
          ...customization.getAllProperties(),
        },
      ),
  },
  {
    type: 'pentagon',
    label: msg`Пятиугольник`,
    icon: <Pentagon size={20} strokeWidth={1} />,
    create: (left, top) =>
      new fabric.Polygon(makePolygonPoints(5, 100, 100, 100, -Math.PI / 2), {
        fill: 'red',
        strokeWidth: 0,
        left,
        top,
        ...customization.getAllProperties(),
      }),
  },
  {
    type: 'hexagon',
    label: msg`Шестиугольник`,
    icon: <Hexagon size={20} strokeWidth={1} />,
    create: (left, top) =>
      new fabric.Polygon(makePolygonPoints(6, 100, 100, 100, 0), {
        fill: 'red',
        strokeWidth: 0,
        left,
        top,
        ...customization.getAllProperties(),
      }),
  },
  {
    type: 'star',
    label: msg`Звезда`,
    icon: <Star size={20} strokeWidth={1} />,
    create: (left, top) =>
      new fabric.Polygon(makeStarPoints(100, 45, 100, 100), {
        fill: 'red',
        strokeWidth: 0,
        left,
        top,
        ...customization.getAllProperties(),
      }),
  },
  {
    type: 'diamond',
    label: msg`Ромб`,
    icon: <Diamond size={20} strokeWidth={1} />,
    create: (left, top) =>
      new fabric.Polygon(
        [
          { x: 100, y: 0 },
          { x: 200, y: 100 },
          { x: 100, y: 200 },
          { x: 0, y: 100 },
        ],
        {
          fill: 'red',
          strokeWidth: 0,
          left,
          top,
          ...customization.getAllProperties(),
        },
      ),
  },
];

export function AddShape() {
  const { t } = useLingui();
  const theme = useTheme();
  const dispatch = useDispatch();
  const activeModal = useSelector(selectActiveModal);
  const fabricManager = useEditorManager();
  const [isSelected, setSelected] = useState(false);
  const [modalState, setModalState] = useState<ModalState>({ isModalOpen: false, modalStyle: {} });

  const closeModals = useCallback(() => {
    setSelected(false);
    setModalState((prev) => ({ ...prev, isModalOpen: false }));
    dispatch(setActiveModal(undefined));
  }, [dispatch]);

  const openShapeModal = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const target = e.currentTarget;
      if (!target) return;

      const bounding = target.getBoundingClientRect();
      setModalState({
        isModalOpen: true,
        modalStyle: {
          top: `${bounding.top}px`,
          left: `${bounding.left + bounding.width + 15}px`,
          width: 300,
        },
      });
      setSelected(true);
      dispatch(setActiveModal('addShape'));
    },
    [dispatch],
  );

  useCloseOnOutsideOrEsc({
    isActive: isSelected,
    isInsideSelectors: ['.modal-button', '.base-modal', '.icon-button-shape'],
    onClose: closeModals,
  });

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isSelected) {
        closeModals();
      } else {
        openShapeModal(e);
      }
    },
    [closeModals, isSelected, openShapeModal],
  );

  const addShape = useCallback(
    (shape: ShapeType) => {
      if (!fabricManager) return;

      const config = SHAPE_CONFIGS.find((c) => c.type === shape);
      if (!config) return;

      history.saveState();
      const { left, top } = fabricManager.getLeftTopCurVP();
      const object = config.create(left, top);

      fabricManager.canvas.add(object);
      SnapToGrid.snapToGrid([object]);
      closeModals();
    },
    [closeModals, fabricManager],
  );

  return (
    <>
      <UDTippy disabled={Boolean(activeModal)} content={t`Фигуры`} size="xs">
        <span style={{ display: 'inline-block' }}>
          <UDIconButton
            variant="ghost"
            className="icon-button-shape"
            icon={<OpenerIconWrapper icon={<Squircle size={20} strokeWidth={1.5} />} />}
            style={{ color: activeModal === 'addShape' ? theme.colors.DarkYellow : undefined }}
            onClick={handleClick}
            tabIndex={0}
          />
        </span>
      </UDTippy>
      <BaseModal className="base-modal" isOpen={modalState.isModalOpen && isSelected} style={modalState.modalStyle}>
        {SHAPE_CONFIGS.map((config) => (
          <ModalButton key={config.type} className="modal-button" onClick={() => addShape(config.type)}>
            {config.icon}
            {t(config.label)}
          </ModalButton>
        ))}
      </BaseModal>
    </>
  );
}
